import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { pushService } from "../services/pushService";
import { nativePushService } from "../services/nativePushService";

/**
 * Hook to initialize background notifications and push service.
 * Should be called early in the app lifecycle (e.g., in App.tsx or a root component).
 *
 * Features:
 * - Registers service worker for offline support and push notifications
 * - Requests notification permission from user
 * - Sets up Web Push subscriptions
 * - Handles browser compatibility gracefully
 */
export const useBackgroundNotifications = (enabled: boolean = true) => {
  const navigate = useNavigate();

  // Listen for the SW's NOTIFICATION_NAVIGATE message (fired when the user
  // clicks a push notif while the PWA is already open on another page).
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMessage = (event: MessageEvent) => {
      const data = event.data;
      if (data && data.type === "NOTIFICATION_NAVIGATE" && data.url) {
        navigate(data.url);
      }
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => {
      navigator.serviceWorker.removeEventListener("message", onMessage);
    };
  }, [navigate]);

  // Native WebView shell (mobile-expo) — forward the Expo push token to the
  // backend. Runs alongside (not instead of) Web Push: the shell short-circuits
  // if the flag isn't present, so browsers keep going through the SW path.
  useEffect(() => {
    if (!enabled) return;
    if (!nativePushService.isNative()) return;
    nativePushService.subscribe().catch((err) =>
      console.warn("[notifications] native subscribe failed", err)
    );
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    // Skip the Web Push path inside the native shell — WebView cannot receive
    // Web Push in the background, and prompting the user for a permission that
    // won't deliver anything is just noise.
    if (nativePushService.isNative()) return;

    const initializeNotifications = async () => {
      try {
        // 1. Register Service Worker (required for push notifications)
        console.log("[notifications] Registering service worker...");
        const registration = await pushService.registerSW();
        if (!registration) {
          console.warn("[notifications] Service worker registration failed");
          return;
        }
        console.log("[notifications] Service worker registered successfully");

        // 2. Request notification permission if not already granted
        if (Notification.permission === "default") {
          console.log("[notifications] Requesting notification permission...");
          const permission = await Notification.requestPermission();
          console.log("[notifications] Notification permission:", permission);

          // 3. Subscribe to push notifications if permission granted
          if (permission === "granted") {
            console.log("[notifications] Permission granted, subscribing to push...");
            const subscribed = await pushService.subscribe();
            if (subscribed) {
              console.log("[notifications] Successfully subscribed to push notifications");
            } else {
              console.warn("[notifications] Push subscription failed");
            }
          }
        } else if (Notification.permission === "granted") {
          // Already granted, just subscribe
          console.log("[notifications] Permission already granted, subscribing to push...");
          const subscribed = await pushService.subscribe();
          if (subscribed) {
            console.log("[notifications] Successfully subscribed to push notifications");
          }
        } else {
          console.log("[notifications] Notification permission denied by user");
        }
      } catch (err) {
        console.error("[notifications] Error during initialization:", err);
      }
    };

    initializeNotifications();
  }, [enabled]);
};
